import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const EDGE_GAP = 8

function actionLabel(control) {
  const override = control.getAttribute('data-context-action')?.trim()
  return override || control.getAttribute('aria-label')?.trim() || control.textContent?.trim() || ''
}

export default function RowContextMenu() {
  const menuRef = useRef(null)
  const [menu, setMenu] = useState(null)

  useEffect(() => {
    const app = document.querySelector('.app.mac')
    if (!app) return undefined

    function open(event) {
      const row = event.target.closest?.('[data-context-row]')
      if (!row || !app.contains(row)) return
      const control = row.querySelector('[data-context-action]')
      if (
        !control ||
        control.disabled ||
        control.getAttribute('aria-disabled') === 'true'
      ) return
      const label = actionLabel(control)
      if (!label) return

      event.preventDefault()
      const origin = row.querySelector('[data-detail-id], .rowopen') || control
      setMenu({
        control,
        label,
        origin,
        left: event.clientX,
        top: event.clientY,
      })
    }

    app.addEventListener('contextmenu', open)
    return () => app.removeEventListener('contextmenu', open)
  }, [])

  useLayoutEffect(() => {
    if (!menu || !menuRef.current) return
    const rect = menuRef.current.getBoundingClientRect()
    const left = Math.max(EDGE_GAP, Math.min(menu.left, window.innerWidth - rect.width - EDGE_GAP))
    const top = Math.max(EDGE_GAP, Math.min(menu.top, window.innerHeight - rect.height - EDGE_GAP))
    if (left !== menu.left || top !== menu.top) {
      setMenu((current) => current ? { ...current, left, top } : current)
      return
    }
    menuRef.current.querySelector('[role="menuitem"]')?.focus()
  }, [menu])

  useEffect(() => {
    if (!menu) return undefined

    function dismissFromOutside(event) {
      if (menuRef.current?.contains(event.target)) return
      setMenu(null)
      if (menu.origin.isConnected) menu.origin.focus({ preventScroll: true })
    }

    function dismissFromMovement() {
      setMenu(null)
    }

    document.addEventListener('pointerdown', dismissFromOutside)
    document.addEventListener('scroll', dismissFromMovement, { capture: true })
    window.addEventListener('resize', dismissFromMovement)
    return () => {
      document.removeEventListener('pointerdown', dismissFromOutside)
      document.removeEventListener('scroll', dismissFromMovement, { capture: true })
      window.removeEventListener('resize', dismissFromMovement)
    }
  }, [menu])

  if (!menu) return null

  function dismiss({ restoreFocus }) {
    const origin = menu.origin
    setMenu(null)
    if (restoreFocus && origin.isConnected) origin.focus({ preventScroll: true })
  }

  function activate() {
    const control = menu.control
    dismiss({ restoreFocus: true })
    window.requestAnimationFrame(() => {
      if (control.isConnected) control.click()
    })
  }

  function onKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      dismiss({ restoreFocus: true })
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      menuRef.current?.querySelector('[role="menuitem"]')?.focus()
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      activate()
    }
  }

  return createPortal(
    <div
      className="row-context-menu"
      role="menu"
      aria-orientation="vertical"
      aria-label="Row actions"
      ref={menuRef}
      style={{ left: menu.left, top: menu.top }}
      onContextMenu={(event) => event.preventDefault()}
      onKeyDown={onKeyDown}
    >
      <button type="button" role="menuitem" onClick={activate}>{menu.label}</button>
    </div>,
    document.body,
  )
}
